import React from 'react';

/**
 * Skeleton Component - Loading skeleton per feedback visivo durante il caricamento
 */

export const Skeleton = ({ className = '', variant = 'rect' }) => {
    const variants = {
        rect: 'h-4',
        circle: 'rounded-full',
        text: 'h-3',
        avatar: 'rounded-full w-12 h-12',
    };

    return (
        <div 
            className={`
                animate-pulse bg-gray-200 dark:bg-gray-700 
                ${variants[variant] || variants.rect}
                ${className}
            `}
        />
    );
};

/**
 * SkeletonCard - Skeleton per cards
 */
export const SkeletonCard = () => {
    return (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 border border-gray-200 dark:border-gray-700">
            <div className="animate-pulse space-y-4">
                {/* Header */}
                <div className="flex items-center gap-3">
                    <Skeleton variant="circle" className="w-12 h-12" />
                    <div className="flex-1 space-y-2">
                        <Skeleton className="h-4 w-3/4" />
                        <Skeleton className="h-3 w-1/2" />
                    </div>
                </div>
                
                {/* Content */}
                <div className="space-y-2">
                    <Skeleton className="h-3 w-full" />
                    <Skeleton className="h-3 w-5/6" />
                    <Skeleton className="h-3 w-4/6" />
                </div>

                {/* Footer */}
                <div className="flex gap-2 justify-end">
                    <Skeleton className="h-9 w-20 rounded-md" />
                    <Skeleton className="h-9 w-24 rounded-md" />
                </div>
            </div>
        </div>
    );
};

/**
 * SkeletonTable - Skeleton per tabelle
 */
export const SkeletonTable = ({ rows = 5 }) => {
    return (
        <div className="space-y-3">
            {Array.from({ length: rows }).map((_, index) => (
                <div key={index} className="flex items-center gap-4 p-4 bg-white dark:bg-gray-800 rounded-lg">
                    <Skeleton variant="circle" className="w-10 h-10" />
                    <div className="flex-1 space-y-2">
                        <Skeleton className="h-4 w-1/3" />
                        <Skeleton className="h-3 w-1/4" />
                    </div>
                    <Skeleton className="h-8 w-20 rounded-md" />
                </div>
            ))}
        </div>
    );
};

/**
 * SkeletonDashboard - Skeleton per dashboard stats
 */
export const SkeletonDashboard = () => {
    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-800 p-8 rounded-xl">
                <div className="space-y-4">
                    <Skeleton className="h-8 w-64 bg-white/20" />
                    <Skeleton className="h-4 w-48 bg-white/20" />
                    
                    {/* Stats cards */}
                    <div className="grid grid-cols-2 laptop-sm:grid-cols-4 gap-4 mt-6">
                        {[1, 2, 3, 4].map((i) => (
                            <div key={i} className="bg-white/20 backdrop-blur-sm rounded-lg p-4">
                                <Skeleton className="h-3 w-20 mb-2 bg-white/30" />
                                <Skeleton className="h-6 w-12 bg-white/30" />
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Content grid */}
            <div className="grid grid-cols-1 laptop-sm:grid-cols-2 gap-6">
                <SkeletonCard />
                <SkeletonCard />
            </div>
        </div>
    );
};

/**
 * SkeletonList - Skeleton per liste di membri
 */
export const SkeletonList = ({ items = 5 }) => {
    return (
        <div className="space-y-2 p-2">
            {Array.from({ length: items }).map((_, index) => (
                <div key={index} className="flex items-center gap-3 p-3 rounded-lg">
                    <Skeleton variant="circle" className="w-8 h-8" />
                    <div className="flex-1 space-y-2">
                        <Skeleton className="h-4 w-32" />
                        <Skeleton className="h-3 w-24" />
                    </div>
                </div>
            ))}
        </div>
    );
};

export default Skeleton;

